import { ExchangeType, FederalTaxPercent, UNIT } from 'src/settings/settings';
import { fundToMutualFundAgreementKey } from '../../utils/module-util';
import { createApiPostActionCreator } from '../../utils/createApiPostActionCreator';
import { toLookup } from 'src/utils/utils';

import { ExchangeTransactionActionType as act } from './actionTypes';
import { ExchangeTransaction } from './types';
import { Fund } from 'src/components/Exchange/types';
export * from './actionTypes';

export const DEFAULT_STATE: ExchangeTransaction = {
  acceptedMarketFluctuationDisclosure: false,
  acceptedRI2020WithholdingDisclosure: false,
  exchangeContributionYear: undefined, // can't default since at this stage don't know if current year is available
  exchangeFromLookup: {},
  exchangeToExistingAllowedAgreementIdLookup: {},
  exchangeToExistingLoadFailed: false,
  exchangeToExistingLoading: false,
  exchangeToExistingLoaded: false,
  exchangeToLookup: {},
  exchangeToNewAllowedTickerLookup: {},
  exchangeToNewFundList: [],
  exchangeToNewLoading: false,
  exchangeType: ExchangeType.ONE_TO_ONE,
  federalWithholding: {
    applied: true,
    percent: FederalTaxPercent.DEFAULT,
  },
  selectedDays: [],
  selectedMonths: [],
  grossAmount: 0,
  grossUnit: UNIT.DOLLARS,
  exchangeFederalWithholdingApplied: true,
  exchangeFederalWithholdingPercent: FederalTaxPercent.DEFAULT,
  electionAgreementUniqueId1: 0,
  electionAgreementUniqueId2: 0,
};

function clearSelections(
  originalLookup: ExchangeTransaction['exchangeFromLookup'],
  leaveFirstSelected?: act
) {
  const lookup = {};
  let foundFirst = false;
  Object.keys(originalLookup).forEach((lookupAgreementId) => {
    let selected = false;
    const originalSelection = originalLookup[lookupAgreementId];
    if (
      leaveFirstSelected === act.LEAVE_FIRST_SELECTED &&
      !foundFirst &&
      originalSelection.selected
    ) {
      selected = true;
      foundFirst = true;
    }
    lookup[lookupAgreementId] = {
      ...originalSelection,
      selected,
    };
  });
  return lookup;
}

function exchangeFromIsSingleSelect(state) {
  const { exchangeType } = state;
  return exchangeType !== ExchangeType.MANY_TO_ONE;
}

function exchangeToIsSingleSelect(state) {
  const { exchangeType } = state;
  return exchangeType !== ExchangeType.ONE_TO_MANY;
}

function exchangeFromMultiRegistrationRestrictor(
  fromLookup: ExchangeTransaction['exchangeFromLookup'],
  registrationGroup: Fund['registrationGroup'],
  fund: Fund
) {
  const newLookup = {};
  Object.entries(fromLookup).forEach(
    ([key, value]) =>
      (newLookup[key] = {
        ...value,
        selected:
          value.selected &&
          (value.registrationGroup === registrationGroup ||
            (value.formattedEntityNames === fund?.formattedEntityNames &&
              value.agreementType === fund?.agreementType &&
              (value.productType === 'MutualFund' ||
                value.productType === 'ManagedAccount'))),
      })
  );
  return newLookup;
}

function exchangeToMultiRegistrationRestrictor(
  toLookup: ExchangeTransaction['exchangeToLookup'],
  registrationGroup: Fund['registrationGroup'],
  fund: Fund
) {
  const newLookup = {};
  Object.entries(toLookup).forEach(([key, value]) => {
    newLookup[key] = {
      ...value,
      selected:
        value.selected &&
        (value.registrationGroup === registrationGroup ||
          (value.formattedEntityNames === fund?.formattedEntityNames &&
            value.agreementType === fund?.agreementType &&
            (value.productType === 'MutualFund' ||
              value.productType === 'ManagedAccount'))),
    };
  });
  return newLookup;
}

const exchangeNewFundListFilter = (fund: Fund) => !fund.iClass;

function createSelectUpdateReducer(
  deselectAction,
  selectAction,
  updateAction,
  stateKey,
  isSingleSelect,
  multiRegistrationRestrictor,
  defaultUnit
) {
  return function exchangeSelectUpdateReducer(state = {}, { payload, type }) {
    switch (type) {
      case selectAction:
      case deselectAction:
      case updateAction: {
        const originalLookup = state[stateKey];
        const { agreementID, registrationGroup, ...restOfPayload } = payload;
        let lookup;
        if (type === selectAction) {
          lookup = isSingleSelect(state)
            ? clearSelections(originalLookup)
            : multiRegistrationRestrictor(
                originalLookup,
                registrationGroup,
                payload.fund
              );
        } else {
          lookup = originalLookup;
        }

        const newCartItem = lookup[agreementID] || {
          ...restOfPayload.fund,
          agreementID,
          grossUnit: defaultUnit,
          registrationGroup,
        };
        newCartItem.selected = type !== deselectAction;

        if (type === updateAction) {
          Object.assign(newCartItem, restOfPayload);
        }
        return {
          ...state,
          [stateKey]: {
            ...lookup,
            [agreementID]: newCartItem,
          },
        };
      }
      case 'RESET_TRANSACTION':
        return {
          ...state,
          acceptedMarketFluctuationDisclosure: false,
          acceptedRI2020WithholdingDisclosure: false,
          exchangeContributionYear: undefined, // can't default since at this stage don't know if current year is available
          exchangeFromLookup: {},
          exchangeToExistingAllowedAgreementIdLookup: {},
          exchangeToExistingLoadFailed: false,
          exchangeToExistingLoading: false,
          exchangeToExistingLoaded: false,
          exchangeToLookup: {},
          exchangeToNewAllowedTickerLookup: {},
          exchangeToNewFundList: [],
          exchangeToNewLoading: false,
          exchangeType: ExchangeType.ONE_TO_ONE,
          federalWithholding: {
            applied: true,
            percent: FederalTaxPercent.MIN,
          },
          selectedDays: [],
          selectedMonths: [],
          grossAmount: 0,
          grossUnit: UNIT.DOLLARS,
          exchangeFederalWithholdingApplied: true,
          exchangeFederalWithholdingPercent: FederalTaxPercent.MIN,
          electionAgreementUniqueId1: 0,
          electionAgreementUniqueId2: 0,
        };
      default:
        return state;
    }
  };
}

const exchangeFromReducer = createSelectUpdateReducer(
  act.DESELECT_EXCHANGE_FROM_FUND,
  act.SELECT_EXCHANGE_FROM_FUND,
  act.UPDATE_EXCHANGE_FROM_FUND,
  'exchangeFromLookup',
  exchangeFromIsSingleSelect,
  exchangeFromMultiRegistrationRestrictor,
  UNIT.DOLLARS
);

const exchangeToReducer = createSelectUpdateReducer(
  act.DESELECT_EXCHANGE_TO_FUND,
  act.SELECT_EXCHANGE_TO_FUND,
  act.UPDATE_EXCHANGE_TO_FUND,
  'exchangeToLookup',
  exchangeToIsSingleSelect,
  exchangeToMultiRegistrationRestrictor,
  UNIT.PERCENT
);

export default function exchangeTransaction(state = DEFAULT_STATE, action) {
  const { type, payload } = action;
  let newState = { ...state };

  switch (type) {
    case act.SELECT_EXCHANGE_TYPE: {
      const { exchangeType } = payload;
      newState = {
        ...state,
        exchangeType,
      };

      if (
        exchangeFromIsSingleSelect(newState) &&
        !exchangeFromIsSingleSelect(state)
      ) {
        newState.exchangeFromLookup = clearSelections(
          state.exchangeFromLookup,
          act.LEAVE_FIRST_SELECTED
        );
      }

      if (
        exchangeToIsSingleSelect(newState) &&
        !exchangeToIsSingleSelect(state)
      ) {
        newState.exchangeToLookup = clearSelections(
          state.exchangeToLookup,
          act.LEAVE_FIRST_SELECTED
        );
      }
      break;
    }

    case act.ACCEPT_RI2020_WITHHOLDING_DISCLOSURE: {
      const { acceptedRI2020WithholdingDisclosure } = payload;
      newState = {
        ...state,
        acceptedRI2020WithholdingDisclosure,
      };
      break;
    }

    case act.UPDATE_MONTH_SELECTION: {
      const { selection } = payload;
      newState = {
        ...state,
        selectedMonths: selection,
      };
      break;
    }

    case act.UPDATE_DAY_SELECTION: {
      const { selection } = payload;
      newState = {
        ...state,
        selectedDays: selection,
      };
      break;
    }

    case act.UPDATE_UNIQUE_IDS: {
      const { electionAgreementUniqueId1, electionAgreementUniqueId2 } =
        payload;
      newState = {
        ...state,
        electionAgreementUniqueId1,
        electionAgreementUniqueId2,
      };
      break;
    }

    case act.UPDATE_UNIT_SELECTION: {
      const { grossUnit } = payload;
      newState = {
        ...state,
        grossUnit,
      };
      break;
    }

    case act.ACCEPT_MARKET_FLUCTUATION_DISCLOSURE: {
      const { acceptedMarketFluctuationDisclosure } = payload;
      newState = {
        ...state,
        acceptedMarketFluctuationDisclosure,
      };
      break;
    }

    case act.UPDATE_EXCHANGE_CONTRIBUTION_YEAR: {
      const { exchangeContributionYear } = payload;
      newState = {
        ...state,
        exchangeContributionYear,
      };
      break;
    }
    case act.UPDATE_EXCHANGE_FEDERAL_WITHHOLDING: {
      // first, destructure the federalWithholding, so we can modify it.
      const { federalWithholding } = state;
      const {
        federalWithholdingApplied: applied,
        federalWithholdingPercent: percent,
        ...restOfPayload
      } = payload;
      newState = {
        ...state,
        ...restOfPayload,
        federalWithholding: {
          ...federalWithholding,
          applied,
          percent,
        },
      };
      break;
    }

    case act.SELECT_EXCHANGE_FROM_FUND:
      newState = {
        ...state,
        exchangeContributionYear: null,
        exchangeToExistingAllowedAgreementIdLookup: {},
        exchangeToNewAllowedTickerLookup: {},
      };
      break;

    case act.ADD_NEW_EXCHANGE_TO_FUND:
      return {
        ...state,
        exchangeToNewFundList: [...state.exchangeToNewFundList, payload.fund],
      };

    case `${act.SEARCH_EXCHANGE_TO_EXISTING}_PENDING`:
      newState = {
        ...state,
        exchangeToExistingLoadFailed: false,
        exchangeToExistingLoading: true,
        exchangeToExistingLoaded: false,
        exchangeToExistingAllowedAgreementIdLookup: {},
      };
      break;

    case `${act.SEARCH_EXCHANGE_TO_EXISTING}_FULFILLED`: {
      const { body } = payload;
      newState = {
        ...state,
        exchangeToExistingLoadFailed: false,
        exchangeToExistingLoading: false,
        exchangeToExistingLoaded: true,
        exchangeToExistingAllowedAgreementIdLookup:
          Array.isArray(body) && body.length
            ? toLookup(
                body,
                (key) => key.agreementID,
                () => true
              )
            : {},
      };
      break;
    }

    case `${act.SEARCH_EXCHANGE_TO_EXISTING}_REJECTED`:
      newState = {
        ...state,
        exchangeToExistingLoadFailed: true,
        exchangeToExistingLoading: false,
        exchangeToExistingLoaded: true,
        exchangeToExistingAllowedAgreementIdLookup: {},
      };
      break;

    case `${act.SEARCH_EXCHANGE_TO_NEW}_PENDING`:
      newState = {
        ...state,
        exchangeToNewLoading: true,
        exchangeToNewAllowedTickerLookup: {},
      };
      break;

    case `${act.SEARCH_EXCHANGE_TO_NEW}_FULFILLED`: {
      const { body } = payload;
      newState = {
        ...state,
        exchangeToNewLoading: false,
        exchangeToNewAllowedTickerLookup:
          Array.isArray(body) && body.length
            ? toLookup(
                body.filter(exchangeNewFundListFilter),
                (key) => key.ticker || key.trpcode,
                () => true
              )
            : {},
      };
      break;
    }

    case act.UPDATE_EXCHANGE_FROM_FUND: {
      // any change to the 'from' fund can cause an individual contribution year to become disabled
      // so always force reselection
      newState = {
        ...state,
        exchangeContributionYear: null,
      };

      const { grossUnit } = payload;
      if (grossUnit) {
        const updatedExchangeToLookup = {};
        Object.keys(state.exchangeToLookup).forEach((lookupAgreementId) => {
          const exchangeToTransactionItem =
            state.exchangeToLookup[lookupAgreementId];
          updatedExchangeToLookup[lookupAgreementId] = {
            ...exchangeToTransactionItem,
            grossUnit:
              grossUnit === UNIT.DOLLARS
                ? exchangeToTransactionItem.grossUnit
                : UNIT.PERCENT,
            grossAmount:
              grossUnit === UNIT.DOLLARS
                ? exchangeToTransactionItem.grossAmount
                : null,
          };
        });
        newState = {
          ...state,
          exchangeToLookup: updatedExchangeToLookup,
        };
      }
      break;
    }
    case `${act.SEARCH_EXCHANGE_TO_NEW}_REJECTED`:
      newState = {
        ...state,
        exchangeToNewLoading: false,
        exchangeToNewAllowedTickerLookup: {},
      };
      break;

    case `${act.SWAP_NEW_EXCHANGE_TO_FUND}`:
      const { agreementID, fund } = payload;
      const { exchangeToLookup, exchangeToNewFundList } = state;
      return {
        ...state,
        exchangeToLookup: {
          ...exchangeToLookup,
          [agreementID]: fund,
        },
        exchangeToNewFundList: exchangeToNewFundList.map((f) =>
          f.agreementID === agreementID ? fund : f
        ),
      };

    default:
      break;
  }

  return exchangeFromReducer(exchangeToReducer(newState, action), action);
}

export function searchExchangeToNew(fund) {
  return createApiPostActionCreator(
    act.SEARCH_EXCHANGE_TO_NEW,
    'exchangeToNewSearch',
    fundToMutualFundAgreementKey(fund)
  );
}

export * from './actions';
